import { rollingEndDate, rollingStartDate } from "@features/run/RunGroupDialog/rolling-date"
import { format, formatISO, isValid, parseISO, startOfMonth } from "date-fns"

/**
 * Supported date inputs for linked-graph validation.
 *
 * - `string` can be ISO or `YYYY/MM/DD`
 * - `Date` is treated as-is
 * - `"rolling"` is resolved using rolling-date helpers
 */
type GraphDateValue = string | Date | null | undefined | "rolling"

/**
 * Raw date inputs plus rolling config used to resolve comparable dates.
 */
export type LinkedGraphDateInput = {
  /**
   * Start date string/date or `"rolling"` to derive from rolling rules.
   *
   * When set to `"rolling"`, `simulationFrequency` is required and `maxLag` is optional.
   */
  startDate?: GraphDateValue
  /**
   * End date string/date or `"rolling"` to derive from rolling rules.
   *
   * When set to `"rolling"`, `simulationFrequency` is required and `rollingDuration` is optional.
   */
  endDate?: GraphDateValue
  /**
   * Frequency used when resolving rolling dates.
   *
   * Required whenever `startDate` or `endDate` is `"rolling"`.
   */
  simulationFrequency?: string
  /**
   * Start month for yearly rolling dates (1-12).
   *
   * Only used when `startDate` is `"rolling"` and `simulationFrequency` is `"yearly"`.
   */
  yearlyRollingStartMonth?: number | null
  /**
   * Duration for rolling end date calculations.
   *
   * Only used when `endDate` is `"rolling"`. Defaults to `1`.
   */
  rollingDuration?: number | string | null
  /**
   * Max lag for rolling start date calculations.
   *
   * Only used when `startDate` is `"rolling"`. Defaults to `0`.
   */
  maxLag?: number
}

/**
 * Normalized values and comparison flags for linked-graph validation.
 */
type LinkedGraphDateComparison = {
  /** Normalized values from the current graph document (client-side). */
  current: {
    start?: string
    end?: string
  }
  /** Normalized values from linked graph metadata (server-side). */
  selected: {
    start?: string
    end?: string
  }
  errors: {
    startDateMissing: boolean
    startDateMismatch: boolean
    endDateMissing: boolean
    endDateOutOfRange: boolean
  }
}

const isValidDateValue = (value: string | Date | null | undefined) => {
  if (!value) return false

  if (value instanceof Date) {
    return isValid(value)
  }

  if (typeof value === "string") {
    return isValid(new Date(value))
  }

  return false
}

/**
 * Formats a UTC calendar date without converting it to the local timezone.
 * Rolling dates are represented as UTC midnight to stay stable across machines.
 */
export function formatUTCDate(value: Date) {
  const year = value.getUTCFullYear()
  const month = String(value.getUTCMonth() + 1).padStart(2, "0")
  const day = String(value.getUTCDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

const isoDate = (value: Date | string | number) => {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value

  return formatISO(new Date(value), { representation: "date" })
}

const normalizeDate = (value: GraphDateValue) => {
  if (!value || value === "rolling") return undefined

  return isValidDateValue(value) ? isoDate(value) : undefined
}

const normalizeStartDate = (value: GraphDateValue) => {
  const normalizedDate = normalizeDate(value)
  if (!normalizedDate) return undefined

  return format(startOfMonth(parseISO(normalizedDate)), "yyyy-MM-dd")
}

const resolveStartDate = (input: LinkedGraphDateInput, now: Date) => {
  if (input.startDate === "rolling") {
    const freq = input.simulationFrequency ?? ""
    const lag = input.maxLag ?? 0

    return formatUTCDate(
      rollingStartDate({
        date: now,
        lag,
        freq: freq as "yearly" | "quarterly" | "monthly" | (string & {}),
        startMonth: input.yearlyRollingStartMonth ?? 1,
      }),
    )
  }

  return normalizeStartDate(input.startDate)
}

const resolveEndDate = (input: LinkedGraphDateInput, _now: Date, startDate?: string) => {
  if (input.endDate === "rolling") {
    if (!startDate) return undefined

    const duration =
      typeof input.rollingDuration === "number" && Number.isFinite(input.rollingDuration)
        ? input.rollingDuration
        : typeof input.rollingDuration === "string" &&
          Number.isFinite(Number.parseFloat(input.rollingDuration))
        ? Number.parseFloat(input.rollingDuration)
        : 1

    return formatUTCDate(
      rollingEndDate({
        date: parseISO(startDate),
        duration,
        freq: input.simulationFrequency ?? "",
      }),
    )
  }

  return normalizeDate(input.endDate)
}

/**
 * Normalizes and compares linked-graph dates.
 *
 * - Resolves rolling dates using `rollingStartDate`/`rollingEndDate`
 * - Normalizes dates to ISO `YYYY-MM-DD`
 * - Flags mismatches and invalid ranges for downstream validation
 *
 * @param current - Raw values from the current graph document (client-side).
 * @param selected - Raw values fetched from linked graph metadata (server-side).
 */
export const compareLinkedGraphDates = (
  current: LinkedGraphDateInput,
  selected: LinkedGraphDateInput,
): LinkedGraphDateComparison => {
  const now = new Date()

  const currentStart = resolveStartDate(current, now)
  const currentEnd = resolveEndDate(current, now, currentStart)

  const selectedStart = resolveStartDate(selected, now)
  const selectedEnd = resolveEndDate(selected, now, selectedStart)

  return {
    current: {
      start: currentStart,
      end: currentEnd,
    },
    selected: {
      start: selectedStart,
      end: selectedEnd,
    },
    errors: {
      startDateMissing: !currentStart,
      startDateMismatch: currentStart !== selectedStart,
      endDateMissing: !currentEnd,
      endDateOutOfRange: !!currentEnd && !!selectedEnd && currentEnd > selectedEnd,
    },
  }
}
